import { BookOpen } from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';
import { HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { OrganizationProfile } from '../../onboarding/types';

interface GeneralContextCardProps {
  profile?: OrganizationProfile;
  isLoading: boolean;
}

// Read-only view of the org's shared "general context". Agents without their
// own customization card fall back to these values, so this section is
// informational only — it exposes no edit controls.
export function GeneralContextCard({ profile, isLoading }: Readonly<GeneralContextCardProps>) {
  const { t } = useTranslation();

  if (isLoading) return null;

  const languages = profile?.languages ?? [];
  const rows: Array<{ label: string; value: string }> = [];

  if (profile?.industry)
    rows.push({ label: t('agents.generalContext.industry'), value: profile.industry });
  if (profile?.productSummary)
    rows.push({ label: t('agents.generalContext.product'), value: profile.productSummary });
  if (profile?.salesMotion)
    rows.push({ label: t('agents.generalContext.salesMotion'), value: profile.salesMotion });
  if (profile?.targetAudience)
    rows.push({ label: t('agents.generalContext.targetAudience'), value: profile.targetAudience });
  if (profile?.tone) rows.push({ label: t('agents.generalContext.tone'), value: profile.tone });
  if (profile?.website)
    rows.push({ label: t('agents.generalContext.website'), value: profile.website });
  if (languages.length > 0)
    rows.push({ label: t('agents.generalContext.languages'), value: languages.join(', ') });

  const isEmpty = rows.length === 0;

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <HStack spacing="sm" align="center">
          <BookOpen size={16} color={colors.primary[400]} strokeWidth={2} />
          <Text variant="h5" color={colors.text.primary} style={styles.title}>
            {t('agents.generalContext.title')}
          </Text>
        </HStack>
      </View>

      <View style={styles.body}>
        <VStack spacing="md">
          <Text variant="caption" color={colors.text.tertiary}>
            {t('agents.generalContext.helper')}
          </Text>

          {isEmpty ? (
            <Text variant="body" color={colors.text.secondary}>
              {t('agents.generalContext.empty')}
            </Text>
          ) : (
            <VStack spacing="sm">
              {rows.map((row) => (
                <VStack key={row.label} spacing="xs">
                  <Text variant="caption" color={colors.text.tertiary}>
                    {row.label}
                  </Text>
                  <Text variant="body" color={colors.text.primary}>
                    {row.value}
                  </Text>
                </VStack>
              ))}
            </VStack>
          )}
        </VStack>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
  title: {
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  body: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
});
